import React, { useState } from 'react';
import * as selectors from '../store/selectors';
import * as actions from '../store/actions';
import { favGroupsClient } from 'apollo';
import { connect, useSelector } from 'react-redux';
import { productsClient } from 'apollo';
import { useQuery } from '@apollo/client';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import BodyFilterModal from 'components/FilterModal';
import CreateGroupConfirmation from 'components/Groups/Confirmation';
import * as filterActions from 'components/FilterModal/store/actions';
import * as filtersSelectors from 'components/FilterModal/store/selectors';

import {
  clientIdSelector,
  currentBrandSelector,
  enterpriseBrandSelector,
} from 'store/global/selectors';

import {
  GetStoresQuery as StoresResult,
  GetStoresQueryVariables as StoresVars,
  GetStoresDocument as StoresQuery,
  GetProductsQuery as ProductsResult,
  GetProductsQueryVariables as ProductsVars,
  GetProductsDocument as ProductsQuery,
} from 'generated/products-graphql';
import {
  FetchFilterGroupDocument as fetchGroupQuery,
  FetchFilterGroupQuery as FetchGroupResult,
  FetchFilterGroupQueryVariables as FetchGroupVars,
} from 'generated/favorites-graphql';
import LoadingSpinner from 'components/LoadingSpinner';
import { GroupType } from 'utils/types';

import { ProductSelection } from 'components/ProductsSelection/types';
import { createLabelsForFilters } from 'components/ProductsSelection/utils';
import CreateGroupBreadcrumb from 'components/PageHeader/CreateGroup/Breadcrumb';
import GroupTypeContainer from 'components/PageHeader/CreateGroup/GroupTypeContainer';
import UpdateGroupAction from '../UpdateAction';
import CreateGroupAction from '../CreateAction';
import GroupName from 'components/PageHeader/CreateGroup/GroupName';
import '../styles.scss';
import { FilterSelectionType } from 'components/FilterModal/types';

interface Props {
  groupId?: string;
  groupName?: string;
  currentStep: number;
  groupType: GroupType;
  show: boolean;
  selection: ProductSelection;
  storesRefecth: () => void;
  itemsRefecth: () => void;
  onGroupNameChange: (string) => void;
  onClose: () => void;
  onContinue(currentStep: number): void;
  onPrevious(currentStep: number): void;
  clearSelections: () => void;
  onResetModal: () => void;
  onGroupSet: (values: any) => void;
  onGroupTypeSelect: (group: GroupType) => void;
  initFilterValues: (values: FilterSelectionType) => void;
}

const CreateEditGroupModal: React.FC<Props> = ({
  groupId,
  groupName,
  currentStep,
  groupType,
  show,
  selection,
  storesRefecth,
  itemsRefecth,
  onGroupSet,
  onGroupNameChange,
  onClose,
  onContinue,
  onPrevious,
  clearSelections,
  onResetModal,
  onGroupTypeSelect,
  initFilterValues,
}) => {
  const clientId = useSelector(clientIdSelector);
  const currentBrand = useSelector(currentBrandSelector);
  const enterpriseBrand = useSelector(enterpriseBrandSelector);
  const [isGroupNameValid, setIsGroupNameValid] = useState(false);

  const {
    loading,
    error,
    data: groupData,
  } = useQuery<FetchGroupResult, FetchGroupVars>(fetchGroupQuery, {
    client: favGroupsClient,
    skip: !groupId,
    variables: { id: groupId },
    fetchPolicy: 'network-only',
    onCompleted: ({ fetchFilterGroup: result }) => {
      const retrieveFilters = { ...result.data };

      const filters = {
        accounts: retrieveFilters.accounts ?? [],
        states: retrieveFilters.states ?? [],
        stores: retrieveFilters.stores ?? [],
        items: retrieveFilters.items ?? [],
      };
      initFilterValues(filters);

      handleChangeGroupName(result.name);
      onGroupSet(result.data);
    },
  });

  const storesParam = groupData?.fetchFilterGroup?.data?.stores;
  const itemsParam = groupData?.fetchFilterGroup?.data?.items;

  const { data: stores, loading: storesLoading } = useQuery<StoresResult, StoresVars>(StoresQuery, {
    client: productsClient,
    variables: {
      clientID: clientId,
      brand: enterpriseBrand ? currentBrand : '',
      stores: storesParam && storesParam.length ? storesParam : [],
      pageable: {
        page: 1,
        pageSize: storesParam && storesParam.length ? storesParam.length : 10,
      },
    },
    skip: !groupId || (storesParam && storesParam.length === 0),
    fetchPolicy: 'network-only',
  });

  const { data: products, loading: productsLoading } = useQuery<ProductsResult, ProductsVars>(
    ProductsQuery,
    {
      client: productsClient,
      variables: {
        clientID: clientId,
        brand: enterpriseBrand ? currentBrand : '',
        products: itemsParam && itemsParam.length ? itemsParam : [],
        pageable: {
          page: 1,
          pageSize: itemsParam && itemsParam.length ? itemsParam.length : 10,
        },
      },
      skip: !groupId || (itemsParam && itemsParam.length === 0),
      fetchPolicy: 'network-only',
    }
  );

  const storesFromGroup = stores?.stores?.data ? stores?.stores?.data : [];
  const productsFromGroup = products?.products?.data ? products?.products?.data : [];

  const renderBody = () => {
    if (groupId !== null) {
      if (error && !loading)
        return <Alert variant="danger">{`Error retrieving group with id: ${groupId}`}</Alert>;
      if (loading || storesLoading || productsLoading)
        return (
          <div className="group-loading-container">
            <LoadingSpinner message="Retrieving group details..." />
          </div>
        );
      return renderContent();
    } else {
      return renderContent();
    }
  };

  const handleChangeGroupName = (name: string) => {
    setIsGroupNameValid(name.trim() !== '');
    onGroupNameChange(name);
  };

  const onNextStep = () => {
    onContinue(currentStep);
  };

  const onPreviousStep = () => {
    onPrevious(currentStep);
  };

  const renderContent = () => {
    switch (currentStep) {
      case 1:
        return <GroupTypeContainer selection={groupType} onSelect={onGroupTypeSelect} />;
      case 2:
        return groupType === 'store' ? (
          <BodyFilterModal
            typeOfFilter="stores"
            id={groupId}
            storesFromEntity={storesFromGroup}
            productsFromEntity={productsFromGroup}
          />
        ) : (
          <BodyFilterModal
            typeOfFilter="items"
            id={groupId}
            storesFromEntity={storesFromGroup}
            productsFromEntity={productsFromGroup}
          />
        );
      case 3:
        let labeledSelection = createLabelsForFilters('stores', storesFromGroup, selection);
        labeledSelection = createLabelsForFilters('items', productsFromGroup, labeledSelection);
        return (
          <GroupName
            value={groupName}
            onNameChange={handleChangeGroupName}
            inputTitle={groupId ? 'Edit Group' : 'New Group'}
            inputSubtitle={`${groupId ? 'Edit' : 'Create'} a group name for your selection`}
            appliedSelection={labeledSelection}
            clientId={clientId}
            currentBrand={enterpriseBrand ? currentBrand : ''}
          />
        );
      default:
        return null;
    }
  };

  const isPreviousAvailable = () => {
    return currentStep > 1 && (!groupId || (groupId && currentStep > 2));
  };

  const isContinueAvailable = () => {
    switch (currentStep) {
      case 1:
        return true;
      case 2:
        if (groupType === 'store') {
          return (
            selection['accounts']?.length ||
            selection['states']?.length ||
            selection['stores']?.length
          );
        } else if (groupType === 'item') {
          return selection['items']?.length;
        }
        break;
      case 3:
        return groupName.length;
    }
    return false;
  };

  const handleClose = () => {
    onClose();
    clearSelections();
    onResetModal();
  };

  return currentStep < 4 ? (
    <Modal
      centered
      dialogClassName="create-edit-groups-modal"
      show={show}
      size={currentStep !== 2 ? 'sm' : 'lg'}
      onHide={handleClose}
    >
      <Modal.Header closeButton>
        <div>
          <Modal.Title as="div" bsPrefix="title">
            {groupId !== null ? groupName : 'Create New Group'}
          </Modal.Title>
          <div className="breadcrumb-container">
            <CreateGroupBreadcrumb
              steps={['Group Type', 'Selections', 'Group Name']}
              stepSelected={currentStep}
            />
          </div>
        </div>
      </Modal.Header>
      <Modal.Body style={{ background: `${currentStep === 1 ? '#F5F5F5' : 'white'}` }}>
        {renderBody()}{' '}
      </Modal.Body>
      <Modal.Footer>
        <Button className="btn-cancel" onClick={handleClose}>
          Cancel
        </Button>
        <Button className="btn-previous" disabled={!isPreviousAvailable()} onClick={onPreviousStep}>
          Previous
        </Button>
        {currentStep < 3 && (
          <Button className="btn-continue" disabled={!isContinueAvailable()} onClick={onNextStep}>
            Continue
          </Button>
        )}
        {currentStep === 3 && groupId === null && (
          <CreateGroupAction
            storesRefecth={storesRefecth}
            itemsRefecth={itemsRefecth}
            isDisabledSaveButton={!isGroupNameValid}
          />
        )}
        {currentStep === 3 && groupId !== null && (
          <UpdateGroupAction
            storesRefecth={storesRefecth}
            itemsRefecth={itemsRefecth}
            isDisabledSaveButton={!isGroupNameValid}
          />
        )}
      </Modal.Footer>
    </Modal>
  ) : (
    <Modal centered show={show} onHide={onClose}>
      <CreateGroupConfirmation name={groupName} />
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  currentStep: selectors.currentStepSelector(state),
  groupType: selectors.groupTypeSelector(state),
  groupName: selectors.groupNameSelector(state),
  groupId: selectors.groupIdSelector(state),
  show: selectors.showSelector(state),
  selection: filtersSelectors.selectionSelector(state),
});

const mapDispatchToProps = (dispatch) => ({
  onGroupSet: (groupData: any) => {
    dispatch(actions.setGroup(groupData));
  },
  onGroupNameChange: (groupName: string) => {
    dispatch(actions.changeGroupName(groupName));
  },
  onClose: () => {
    dispatch(actions.setShow(false));
  },
  onContinue: (currentStep) => {
    dispatch(actions.setCurrentStep(currentStep + 1));
  },
  onPrevious: (currentStep) => {
    dispatch(actions.setCurrentStep(currentStep - 1));
  },
  onGroupTypeSelect: (group) => {
    dispatch(actions.setGroupType(group));
  },
  onResetModal: () => {
    dispatch(actions.resetModal());
  },
  clearSelections: () => {
    dispatch(filterActions.clearSelections());
  },
  initFilterValues: (values: FilterSelectionType) => {
    dispatch(filterActions.initValues(values));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateEditGroupModal);
